// React import
import React from 'react';

// Generic Imports
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { create, all } from 'mathjs'
import pluralize from 'pluralize';

// MUI Imports
import Box from '@mui/material/Box';
import { red } from '@mui/material/colors';

// Local Imports
import currencyToText from '../CurrencyToText';
import { calcFieldValue } from './opportunity.js';
import { getTotalRates } from '../dashboard/utils/rates.js';

const math = create(all)
math.import({
    sp_flt: function(params, props) {
        const obj = JSON.parse(params);
        return parseFloat(obj.BasePrice);
    },
    sp_unt: function(params, x) {
        const obj = JSON.parse(params);
        return parseFloat(obj.PerUnitPrice * x);
    }
});
const evaluate = (formula, props) => {
    if (formula.startsWith('sp_')) {
        return math.evaluate(formula.replace(/\)/, `, ${props.x})`), props);
    }
    return math.evaluate(formula, props);
};


function formulaToReact({ formula, errorValue, currency, multiplierText, sections, width, fieldType, buyingRatesCalculation, sx, suffix }) {
    const fraction = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    if (formula.startsWith('sp_')) {
        return (
            <Box sx={sx || { whiteSpace: 'pre-wrap', width: width || '125px' }}>
                Cargowise <b>{cargowiseFuncToDescription({ func: formula })}</b> formula.{suffix ? ` ${suffix}` : ''}
            </Box>
        );
    }

    const lclWM = parseLCLWMFormula(formula);
    if (lclWM) {
        const cur = currency ? currencyToText(currency, true, '') : '';
        const samePrice = lclWM.perTon === lclWM.perCbm;
        const priceText = samePrice
            ? `${cur}${lclWM.perTon}/ton or cbm`
            : `${cur}${lclWM.perTon}/ton or ${cur}${lclWM.perCbm}/cbm`;
        const sameMin = Boolean(lclWM.minPerTon) && lclWM.minPerTon === lclWM.minPerCbm;
        const hasMin = Boolean(lclWM.minPerTon || lclWM.minPerCbm);
        return (
            <Box sx={sx || { whiteSpace: 'pre-wrap', width: width || '125px' }}>
                <b>{priceText}</b>
                {sameMin && <span>&nbsp;with a minimum of <b>{cur}{lclWM.minPerTon}</b></span>}
                {!sameMin && hasMin && (
                    <span>
                        &nbsp;with a minimum of{' '}
                        {lclWM.minPerTon ? <><b>{cur}{lclWM.minPerTon}</b>/ton</> : null}
                        {lclWM.minPerTon && lclWM.minPerCbm ? ' or ' : ''}
                        {lclWM.minPerCbm ? <><b>{cur}{lclWM.minPerCbm}</b>/cbm</> : null}
                    </span>
                )}
                {suffix ? ` ${suffix}` : ''}
            </Box>
        );
    }

    const attempt = formulaToParams({ formula });
    if (!attempt) return errorValue;

    if (attempt.buying) {
        const marginPercent = (attempt.buying.value - 1) * 100;
        const hasConstant = attempt.buying.constant && Number(attempt.buying.constant) !== 0;
        const hasMinimum = attempt.minimum && Number(attempt.minimum) !== 0;

        let formulaDescription;

        if (marginPercent === 0 && hasConstant) {
            // Only a constant on top of the buying rate
            formulaDescription = (
                <Box>
                    {currencyToText(currency, true, fraction.format(attempt.buying.constant).toLocaleString())} on top of the buying rate
                    {hasMinimum ? `, with a minimum of ${currencyToText(currency, true, fraction.format(attempt.minimum).toLocaleString())}.` : '.'}
                    {suffix ? ` ${suffix}` : ''}
                </Box>
            );
        } else {
            const constantText = hasConstant ? ` plus ${currencyToText(currency, true, fraction.format(attempt.buying.constant).toLocaleString())}` : '';
            formulaDescription = (
                <Box>
                    Uplift of {fraction.format(marginPercent)}% on the buying rate{constantText}
                    {hasMinimum ? `, with a minimum of ${currencyToText(currency, true, fraction.format(attempt.minimum).toLocaleString())}.` : '.'}
                    {suffix ? ` ${suffix}` : ''}
                </Box>
            );
        }
            
        let formulaCalculatedValue = null;
        if (buyingRatesCalculation) {
            const { buyingRates, products, organization, transportationMethod, field, exchangeRates, hideDescription } = buyingRatesCalculation;
            const fixedValue = (() => {
                const noProducts = [{ ...products[0], quantity: 0 }];
                const value = calcFieldValue({ field, products: noProducts, transportationMethod, sections, buyingRates, fieldBuyingRates: (field || {}).buyingRates || [], exchangeRates, lockedExchangeRates: {}, organization });
                return value.sum;
            })();
            const oneValue = (() => {
                const noProducts = [{ ...products[0], quantity: 1 }];
                const value = calcFieldValue({ field, products: noProducts, transportationMethod, sections, exchangeRates, lockedExchangeRates: {}, organization, buyingRates, fieldBuyingRates: (field || {}).buyingRates || [] });
                return _.mapValues(value.sum, (v, currency) => (v - (fixedValue[currency] || 0)));
            })();

            const fixedStr = _.every(fixedValue, v => !v) ? null : _.map(fixedValue, (v, currency) => currencyToText(currency, true, Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(v)));
            const oneStr = _.every(oneValue, v => !v) ? null : _.map(oneValue, (v, currency) => currencyToText(currency, true, Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(v)));

            if (hideDescription) formulaDescription = null;

            formulaCalculatedValue = (
                <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '6px' }}>
                    <Box sx={{ fontSize: formulaDescription ? '11px' : '13px' }}>
                        {(hideDescription || (!fixedStr && !oneStr)) ? '' : 'Calculated as '}{fixedStr ? `${fixedStr.join(', ')} per shipment ` : ''}{fixedStr && oneStr ? 'and ' : ''}{oneStr ? `${oneStr.join(', ')} per ${pluralize(multiplierText, 1).replace(/\(s\)$/, '')}` : ''}
                    </Box>
                </Box>
            );
        }
        return (
            <Box sx={sx || { whiteSpace: 'pre-wrap', width: width || '125px' }}>
                {formulaDescription ? formulaDescription : null}
                {formulaCalculatedValue ? formulaCalculatedValue : null}
            </Box>
        );
    }

    if (attempt.fixed) {
        return (
            <Box sx={sx || { whiteSpace: 'pre-wrap', width: width || '125px' }}>
                <b>{currencyToText(currency, true, fraction.format(attempt.fixed.value).toLocaleString())}{attempt.fixed.type === 'flat' ? ' flat' : `/${multiplierText}`}</b>
                {attempt.minimum ? <span>&nbsp;with a minimum of <b>{currencyToText(currency, true, fraction.format(attempt.minimum).toLocaleString())}</b></span> : null}
                {suffix ? ` ${suffix}` : ''}
            </Box>
        );
    }

    if (attempt.tiers) {
        return (
            <Box>
                <Box>
                    <b>Minimum</b>: {currencyToText(currency, true, attempt.minimum)}
                </Box>
                {attempt.tiers.map((t, i) => (
                    <Box>
                        <b>{i === 0 ? `First ${t.step} ${multiplierText}` : `${attempt.tiers[i-1].step} ${multiplierText} - ${i === attempt.tiers.length - 1 ? '∞' : t.step + ' ' + multiplierText}`}</b>: {currencyToText(currency, true, t.value)}{t.type === 'flat' ? ' flat' : `/${multiplierText}`}
                    </Box>
                ))}
                <Box sx={{ fontStyle: 'italic', opacity: 0.7, fontWeight: 400 }}>
                    {attempt.acc
                        ? 'Accumulative — each band applies only to its portion (bands stack)'
                        : `Non-accumulative — the whole amount is charged at the band it falls into`}
                </Box>
                {suffix ? ` ${suffix}` : ''}
            </Box>
        );
    }

    if (attempt.tax) {
        const allFields = (sections || []).reduce((acc, section) => {
            const offers = section.offers || [{ fields: section.fields }];
            offers.forEach(offer => {
                offer.fields.forEach(field => {
                    acc[field.id] = field.title;
                });
            });
            return acc;
        }, {});
        return (
            <Box sx={sx || { whiteSpace: 'normal', fontWeight: 800 }}>
                {attempt.tax.percentage}% of {attempt.tax.fields.map(f => allFields[f]).join(', ')}{attempt.minimum ? ` with a minimum of ${currencyToText(currency, true, attempt.minimum)}` : ''}
                {!_.isUndefined(fieldType) && fieldType !== 'flat' && (
                    <Box sx={{ color: red[500], fontSize: '9px' }}>Tax formulas can only be used in fields that are priced per shipment.</Box>
                )}
            </Box>
        );
    }

    return null;
}

function formulaToText({ formula, errorValue, currency, multiplierText, sections, cost }) {
    if (formula.startsWith('sp_')) {
        return `Cargowise ${cargowiseFuncToDescription({ func: formula })} formula.`;
    }

    const lclWM = parseLCLWMFormula(formula);
    if (lclWM) {
        return lclWMFormulaToText({ formula, currency });
    }

    const attempt = formulaToParams({ formula });
    if (!attempt) return errorValue;

    if (attempt.buying) {
        return currencyToText(currency, true, evaluate(formula, { buying: cost || 0 }).toLocaleString());
    }

    if (attempt.fixed) {
        return `${currencyToText(currency, true, attempt.fixed.value)}${attempt.fixed.type === 'flat' ? ' flat' : `/${multiplierText}`}${attempt.minimum ? ` with a minimum of ${currencyToText(currency, true, attempt.minimum)}` : ''}`;
    }

    if (attempt.tiers) {
        return `Minimum: ${currencyToText(currency, true, attempt.minimum)}
                ${attempt.tiers.map((t, i) => (
                    `${i === 0 ? `First ${t.step} ${multiplierText}` : `${attempt.tiers[i-1].step} ${multiplierText} - ${i === attempt.tiers.length - 1 ? '∞' : t.step + ' ' + multiplierText}`}: ${currencyToText(currency, true, t.value)}${t.type === 'flat' ? ' flat' : `/${multiplierText}`}`
                )).join('\n')}
                ${attempt.acc ? '(Accumulative — bands stack, each applies only to its portion)' : '(Non-accumulative — whole amount charged at the band it falls into)'}`;
    }

    if (attempt.tax) {
        const allFields = (sections || []).reduce((acc, section) => {
            const offers = section.offers || [{ fields: section.fields }];
            offers.forEach(offer => {
                offer.fields.forEach(field => {
                    acc[field.id] = field.title;
                });
            });
            return acc;
        }, {});
        return `${attempt.tax.percentage}% of ${attempt.tax.fields.map(f => allFields[f]).join(', ')}${attempt.minimum ? ` with a minimum of ${currencyToText(currency, true, attempt.minimum)}` : ''}`;
    }

    return null;
}

function formulaToParams({ formula }) {
    const maxMatch = formula.match(/^max\(((\d+\.)?\d+)\,(.*)\)$/);
    if (!maxMatch || maxMatch.length !== 4) {
        return null;
    }
    const minimum = parseFloat(maxMatch[1]);

    // The expression the minimum guards. Trimmed because these formulas are written by importers
    // and by hand as well as by formulaFromMinAndTiers: a space after the comma is invisible to a
    // reader but fatal to an anchored `^x` match, and a formula that fails to parse here is shown
    // to the user as the literal word "formula" in place of its rate.
    const expr = maxMatch[3].trim();

    let tiers = null;
    let acc = false;
    let fixed = null;
    let tax = null;
    let buying = null;

    const buyingMargin = expr.match(/^buying\s*\*\s*((?:\d+\.?\d*|\.\d+))(?:\s*\+\s*((?:\d+\.?\d*|\.\d+)))?$/);
    if (buyingMargin) {
        buying = { value: buyingMargin[1], type: 'per-item' };
        if (buyingMargin[2]) {
            buying.constant = parseFloat(buyingMargin[2]);
        }
    }

    if (!buying) {
        // Multiplication commutes, so read the rate from either side of the multiplier.
        const fixedPerItem = expr.match(/^x\s*\*\s*((\d+\.)?\d+)$/) || expr.match(/^((\d+\.)?\d+)\s*\*\s*x$/);
        if (fixedPerItem) {
            // As a number, not the text it was matched from. A rate written with a leading zero
            // was carried through to the screen as typed and shown as "$0332/shipment", which
            // reads as neither the rate it is nor a number anyone wrote. The minimum beside it
            // was already parsed, so the two disagreed on the same line.
            fixed = { value: parseFloat(fixedPerItem[1]), type: 'per-item' };
        } else {
            const fixedFlat = expr.match(/^((\d+\.)?\d+)$/);
            if (fixedFlat) {
                fixed = { value: parseFloat(fixedFlat[1]), type: 'flat' };
            }
        }
    }

    if (!fixed && !buying) {
        try {
            const _tiers = expr.split(':').map(_.trim);
            tiers = _tiers.map((tier, index) => {
                let match;
                if (match = tier.match(/^x\s*<\s*((\d+\.)?\d+)\s*\?\s*x\s*\*\s*((\d+\.)?\d+)$/)) {
                    return ({ id: uuidv4(), step: parseFloat(match[1]), value: parseFloat(match[3]), type: 'per-item' }); 
                } else if (match = tier.match(/^x\s*<\s*((\d+\.)?\d+)\s*\?\s*((\d+\.)?\d+)$/)) {
                    return ({ id: uuidv4(), step: parseFloat(match[1]), value: parseFloat(match[3]), type: 'flat' }); 
                } else if ((index === _tiers.length - 1) && (match = tier.match(/^x\s*\*\s*((\d+\.)?\d+)$/))) {
                    return ({ id: uuidv4(), value: parseFloat(match[1]), type: 'per-item' }); 
                } else if ((index === _tiers.length - 1) && (match = tier.match(/^((\d+\.)?\d+)$/))) {
                    return ({ id: uuidv4(), value: parseFloat(match[1]), type: 'flat' }); 
                } else {
                    throw new Error('Not Matched');
                }
            })
        } catch (e) {}
    }

    if (!fixed && !buying && !tiers) {
        const _tiers = expr.split('+').map(_.trim);
        tiers = [];

        _tiers.forEach((tier, index) => {
            let match;
            if ((index === 0) && (match = tier.match(/^\(((\d+\.)?\d+)\)/))) {
                tiers.push({ id: uuidv4(), value: parseFloat(match[1]), type: 'flat' }); 
            } else if ((index === 0) && (match = tier.match(/^\(min\(x, ((\d+\.)?\d+)\) \* ((\d+\.)?\d+)\)/))) {
                tiers.push({ id: uuidv4(), step: parseFloat(match[1]), value: parseFloat(match[3]), type: 'per-item' }); 
            } else if ((index > 0) && (match = tier.match(/^\(x > ((\d+\.)?\d+) \? ((\d+\.)?\d+) : 0\)$/))) {
                const prevStep = parseFloat(match[1]);
                const value = parseFloat(match[3]);
                tiers[tiers.length - 1].step = prevStep;
                tiers.push({ id: uuidv4(), value, type: 'flat' }); 
            } else if ((index > 0) && (index < _tiers.length - 1) && (match = tier.match(/^\(x > ((\d+\.)?\d+) \? min\(x - ((\d+\.)?\d+), ((\d+\.)?\d+) - ((\d+\.)?\d+)\) \* ((\d+\.)?\d+) : 0\)$/))) {
                if ((match[1] !== match[3]) || (match[1] !== match[7])) {
                    console.log('@@@@@@@@@ WEIRD');
                }
                const prevStep = parseFloat(match[1]);
                const step = parseFloat(match[5]);
                const value = parseFloat(match[9]);
                tiers[tiers.length - 1].step = prevStep;
                tiers.push({ id: uuidv4(), step, value, type: 'per-item' }); 
            } else if ((index === _tiers.length - 1) && (match = tier.match(/^\(x > ((\d+\.)?\d+) \? \(x - ((\d+\.)?\d+)\) \* ((\d+\.)?\d+) : 0\)$/))) {
                const prevStep = parseFloat(match[1]);
                const value = parseFloat(match[5]);
                tiers[tiers.length - 1].step = prevStep;
                tiers.push({ id: uuidv4(), value, type: 'per-item' }); 
            } else {
                tiers = null;
            }
        })
        // The '+'-joined form (min(x, step) * rate + (x > step ? ... : 0) + ...) is the
        // accumulative representation — tiers stack like tax brackets. Mark it so the editor
        // round-trips the checkbox and the displays can render the stacked breakdown.
        if (tiers) acc = true;
    }

    if (!fixed && !buying && !tiers) {
        const _tax = expr.match(/([\d.]+)\/(\d+)\*(\((?:\(field_([a-f0-9_]+)\)\+)*\(field_([a-f0-9_]+)\)\))/);
        if (_tax) {
            const percentage = parseFloat(_tax[1]);
            const fields = _tax[3].split('+').map(_.trim).map(field => field.match(/field_([a-f0-9_]+)/)[1]);
            tax = { percentage, fields: fields.map(f => f.replace(/_/g, '-')) };
        }
    }

    if (tiers === null && fixed === null && tax === null && buying === null) return null;

    return { minimum, fixed, tiers, tax, acc, buying };
}

// `visiting` holds the fields already being worked out along this chain. A formula that
// refers back to itself - directly, or through another field that refers to it - would
// otherwise recurse until the stack overflows, and where the overflow lands depends on how
// deep the caller already was, so the same quote priced differently on every screen and on
// every run. A field met again on its own chain counts as nothing.
function sectionsToTaxParams({ fields, sections, products, transportationMethod, targetCurrency, exchangeRates, organization, buyingRates, visiting = new Set() }) {
    const params = (sections || []).reduce((acc, section) => {
        const offers = section.offers || [{ fields: section.fields }];
        offers.forEach(offer => {
            offer.fields.forEach(field => {
                if (fields.includes(`field_${field.id.replace(/-/g, '_')}`)) {
                    if (visiting.has(field.id)) {
                        acc['field_' + field.id.replace(/-/g, '_')] = 0;
                        return;
                    }
                    // Without exchangeRates/buyingRates a referenced field can't resolve its own
                    // currency conversion or (for buying-margin formulas) its cost, silently
                    // contributing 0 to the tax sum instead of its real value.
                    const { sum } = calcFieldValue({ field, sections, products, transportationMethod, organization, exchangeRates, buyingRates, fieldBuyingRates: field.buyingRates, visiting }) || {};
                    if (sum) {
                        const normalized = _.sumBy(_.toPairs(sum), ([currency, val]) => val / exchangeRates[currency]);
                        acc['field_' + field.id.replace(/-/g, '_')] = normalized * exchangeRates[targetCurrency] || 0;
                    }
                }
            });
        });
        return acc;
    }, {});
    return params;
}

function formulaFromMinAndTiers({ min, fixed, tiers, acc, tax, buying }) {
    // TODO, check input
    let s = '';
    s += `max(${min},`;
    if (buying) {
        s += `buying * ${buying.value}`;
        if (buying.constant) {
            s += ` + ${buying.constant}`;
        }
    } else if (fixed) {
        if (fixed.type === 'per-item') {
            s += `x * ${fixed.value}`;
        } else {
            s += `${fixed.value}`;
        }
    } else if (tiers) {
        if (tiers.length === 0) return min;
        if (acc) {
            s += tiers.map((tier, index) => {
                if (index === 0) {
                    if (tier.type === 'flat') {
                        return `(${tier.value})`;
                    } else {
                        return `(min(x, ${tier.step}) * ${tier.value})`
                    }
                } else if (index === tiers.length - 1) {
                    const prevStep = tiers[index-1].step;
                    if (tier.type === 'flat') {
                        return `(x > ${prevStep} ? ${tier.value} : 0)`
                    } else {
                        return `(x > ${prevStep} ? (x - ${prevStep}) * ${tier.value} : 0)`
                    }
                } else {
                    const prevStep = tiers[index-1].step;
                    if (tier.type === 'flat') {
                        return `(x > ${prevStep} ? ${tier.value} : 0)`
                    } else {
                        return `(x > ${prevStep} ? min(x - ${prevStep}, ${tier.step} - ${prevStep}) * ${tier.value} : 0)`
                    }
                }
            }).join(' + ');
        } else {
            s += tiers.map((tier, index) => {
                if (index === tiers.length - 1) {
                    return tier.type === 'flat' ? tier.value : `x * ${tier.value}`;
                } else {
                    return `x < ${tier.step} ? ${tier.type === 'flat' ? tier.value : 'x * ' + tier.value} : `;
                }
            }).join('');
        }
    } else if (tax) {
        s += `${tax.percentage}/100*(${tax.fields.map(id => `(field_${id.replace(/-/g, '_')})`).join('+')})`;
    }
    s += ')';
    return s;
}

function addMarginToFormula({ formula, margin }) {
    if (!formula) return null;
    const params = formulaToParams({ formula });
    if (!params) return `(${formula}) * (1 + ${margin})`;
    const { minimum, fixed, tiers, acc, tax } = params;

    return formulaFromMinAndTiers({ 
        min: minimum * (1 + margin), 
        fixed: fixed ? { ...fixed, value: fixed.value * (1 + margin) } : null, 
        tiers: tiers ? tiers.map(tier => ({ ...tier, value: tier.value * (1 + margin) })) : null, 
        acc, 
        tax 
    });
}

/**
 * Generate an LCL W/M formula from per-ton, per-cbm rates and optional minimums.
 * Returns the higher of (perCbm * cbm) and (perTon * tons), with optional minimums.
 * Without minimums: max(perCbm * cbm, perTon * tons)
 * With minimums: max(minPerCbm, minPerTon, perCbm * cbm, perTon * tons)
 */
function formulaFromLCLWM({ perTon, perCbm, densityRatio = 1000, minPerTon = 0, minPerCbm = 0 }) {
    const parts = [];
    if (minPerCbm) parts.push(`${minPerCbm}`);
    if (minPerTon) parts.push(`${minPerTon}`);
    parts.push(`${perCbm} * cbm`);
    parts.push(`${perTon} * tons`);
    return `max(${parts.join(', ')})`;
}

/**
 * Parse an LCL W/M formula back into its components.
 * Returns { perTon, perCbm, minPerTon, minPerCbm } or null if not an LCL W/M formula.
 * Supports both new format max(perCbm * cbm, perTon * tons) and legacy format cbm * DR > weightKg ? ...
 */
function parseLCLWMFormula(formula) {
    // New format: max(perCbm * cbm, perTon * tons)
    const simpleMax = formula.match(/^max\(\s*([\d.]+)\s*\*\s*cbm\s*,\s*([\d.]+)\s*\*\s*tons\s*\)$/);
    if (simpleMax) {
        return { perCbm: parseFloat(simpleMax[1]), perTon: parseFloat(simpleMax[2]), minPerCbm: 0, minPerTon: 0 };
    }
    // New format with minimums: max(minCbm, minTon, perCbm * cbm, perTon * tons) or subsets
    const maxMatch = formula.match(/^max\(\s*(.*)\s*\)$/);
    if (maxMatch) {
        const parts = maxMatch[1].split(',').map(s => s.trim());
        let perCbm = 0, perTon = 0, minPerCbm = 0, minPerTon = 0;
        const mins = [];
        for (const part of parts) {
            const cbmMatch = part.match(/^([\d.]+)\s*\*\s*cbm$/);
            if (cbmMatch) { perCbm = parseFloat(cbmMatch[1]); continue; }
            const tonMatch = part.match(/^([\d.]+)\s*\*\s*tons$/);
            if (tonMatch) { perTon = parseFloat(tonMatch[1]); continue; }
            const numMatch = part.match(/^([\d.]+)$/);
            if (numMatch) { mins.push(parseFloat(numMatch[1])); continue; }
            return null; // Unknown part
        }
        if (!perCbm && !perTon) return null;
        // Assign mins: first to cbm, second to ton (or both to larger)
        if (mins.length >= 2) { minPerCbm = mins[0]; minPerTon = mins[1]; }
        else if (mins.length === 1) { minPerCbm = mins[0]; minPerTon = mins[0]; }
        return { perCbm, perTon, minPerCbm, minPerTon };
    }
    // Legacy format: cbm * DR > weightKg ? perCbm * cbm : perTon * tons
    const legacySimple = formula.match(/^cbm\s*\*\s*([\d.]+)\s*>\s*weightKg\s*\?\s*([\d.]+)\s*\*\s*cbm\s*:\s*([\d.]+)\s*\*\s*tons$/);
    if (legacySimple) {
        return { perCbm: parseFloat(legacySimple[2]), perTon: parseFloat(legacySimple[3]), minPerCbm: 0, minPerTon: 0 };
    }
    // Legacy with minimums
    const legacyFull = formula.match(/^cbm\s*\*\s*([\d.]+)\s*>\s*weightKg\s*\?\s*max\(([\d.]+),\s*([\d.]+)\s*\*\s*cbm\)\s*:\s*max\(([\d.]+),\s*([\d.]+)\s*\*\s*tons\)$/);
    if (legacyFull) {
        return { perCbm: parseFloat(legacyFull[3]), perTon: parseFloat(legacyFull[5]), minPerCbm: parseFloat(legacyFull[2]), minPerTon: parseFloat(legacyFull[4]) };
    }
    return null;
}

/**
 * Format an LCL W/M formula for display.
 */
function lclWMFormulaToText({ formula, currency }) {
    const parsed = parseLCLWMFormula(formula);
    if (!parsed) return null;
    const cur = currency ? currencyToText(currency, true, '') : '';
    const samePrice = parsed.perTon === parsed.perCbm;
    let text = samePrice
        ? `${cur}${parsed.perTon}/ton or cbm`
        : `${cur}${parsed.perTon}/ton or ${cur}${parsed.perCbm}/cbm`;

    const sameMin = parsed.minPerTon && parsed.minPerTon === parsed.minPerCbm;
    if (sameMin) {
        text += ` [min ${cur}${parsed.minPerTon}]`;
    } else {
        const mins = [];
        if (parsed.minPerTon) mins.push(`min ${cur}${parsed.minPerTon}/ton`);
        if (parsed.minPerCbm) mins.push(`min ${cur}${parsed.minPerCbm}/cbm`);
        if (mins.length) text += ` [${mins.join(', ')}]`;
    }
    return text;
}

export { formulaToParams, formulaToReact, sectionsToTaxParams, evaluate, formulaFromMinAndTiers, formulaToText, addMarginToFormula, formulaFromLCLWM, parseLCLWMFormula, lclWMFormulaToText };

function cargowiseFuncToDescription({ func }) {
    const actualFunc = func.substr(3).split('(')[0];
    switch (actualFunc) {
        case 'flt': return 'Flat Rate';
        case 'unt': return 'Per Unit Price';
        case 'agy': return 'Agency Fee';
        default: return actualFunc;
    }
}
